import * as React from 'react';
import { useState } from 'react';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { EventTypes } from '~analytics/types';
import { translate } from '~l10n/client';
import { SmallButton } from '~shared/components/buttons';
import { ChipButton } from '~shared/components/buttons/ChipButton';
import { Dialog } from '~shared/components/Dialog/Dialog';
import { Divider } from '~shared/components/Divider';
import Textarea from '~shared/components/Textarea/Textarea';
import { toast } from '~shared/components/Toast';

export type RateQuestionQualitySource =
  | 'quiz_editor'
  | 'areas_of_improvement'
  | 'specific_students';

export interface RateQuestionQualityAiDetails {
  promptVersion?: string;
  insightId?: string;
}

interface RateQuestionQualityFeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  source: RateQuestionQualitySource;
  aiDetails?: RateQuestionQualityAiDetails;
}

type FeedbackReason =
  | 'too-easy'
  | 'too-hard'
  | 'inaccurate'
  | 'poorly-worded'
  | 'not-aligned'
  | 'weak-distractors'
  | 'other';

const feedbackOptions = [
  { value: 'too-easy', label: () => translate('Too easy') },
  { value: 'too-hard', label: () => translate('Too hard') },
  { value: 'inaccurate', label: () => translate('Inaccurate') },
  { value: 'poorly-worded', label: () => translate('Poorly worded / unclear') },
  {
    value: 'not-aligned',
    label: () => translate('Not aligned to my objective'),
  },
  { value: 'weak-distractors', label: () => translate('Distractors are weak') },
  { value: 'other', label: () => translate('Other') },
] as const;

export function RateQuestionQualityFeedbackDialog({
  open,
  onClose,
  source,
  aiDetails,
}: RateQuestionQualityFeedbackDialogProps) {
  const [selectedReasons, setSelectedReasons] = useState<FeedbackReason[]>([]);
  const [feedbackNote, setFeedbackNote] = useState('');

  const toggleReason = (reason: FeedbackReason) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  const handleSubmit = () => {
    sendTeacherAmplitudeEvent(EventTypes.RateQuestionQuality, {
      question_quality_rating: 'not-helpful',
      feedback_reasons: selectedReasons,
      feedback_note: feedbackNote || undefined,
      source,
      details: {
        insight_id: aiDetails?.insightId,
        ai_prompt_version: aiDetails?.promptVersion,
      },
    });

    toast(translate('Thanks for your feedback!'), { tone: 'positive' });

    setSelectedReasons([]);
    setFeedbackNote('');
    onClose();
  };

  const handleClose = () => {
    setSelectedReasons([]);
    setFeedbackNote('');
    onClose();
  };

  return (
    <Dialog
      id="rate-question-quality-container"
      open={open}
      onClose={handleClose}
      title={translate('Question Quality')}
      showBackdrop
    >
      <div className="flex flex-col gap-4 mt-4 text-base text-blue-900">
        <Divider />
        <div>
          <label className="block mb-3">
            {translate('Provide Additional Feedback')}
          </label>
          <div
            className="flex flex-wrap gap-2"
            role="group"
            aria-label={translate('Provide Additional Feedback')}
          >
            {feedbackOptions.map((option) => (
              <ChipButton
                key={option.value}
                isSelected={selectedReasons.includes(option.value)}
                onClick={() => toggleReason(option.value)}
              >
                {option.label()}
              </ChipButton>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="feedback-note" className="block mb-2">
            {translate('Add a note (optional)')}
          </label>
          <Textarea
            id="feedback-note"
            value={feedbackNote}
            onChange={(e) => setFeedbackNote(e.target.value)}
            minRows={1}
            maxLength={1000}
            className="w-full"
          />
          <p className="text-xs mt-2">
            {translate('Please avoid sharing personal or sensitive info.')}
          </p>
        </div>

        <div className="flex justify-end">
          <SmallButton
            onClick={handleSubmit}
            isDisabled={selectedReasons.length === 0}
          >
            {translate('Submit')}
          </SmallButton>
        </div>
      </div>
    </Dialog>
  );
}
